
// src/context/UserProfileContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";

// Display-only fields — this is exactly what interceptor.js's verifyToken persists
// to localStorage. Deliberately excludes is_admin/is_superuser/account_access_level
// and other authorization-shaped fields from the SSO response: nothing in this app
// treats them as real permission checks (those go through get_user_role/
// PermissionContext instead), and this profile is stored client-side where it's
// trivially readable/writable — so no field here should ever gate access to anything.
interface UserProfile {
  user_id: string;
  email: string;
  firstname: string;
  lastname: string;
  alias_name: string;
}

interface UserProfileContextType {
  profile: UserProfile | null;
  setProfile: (profile: UserProfile | null) => void;
}

const UserProfileContext = createContext<UserProfileContextType>({
  profile: null,
  setProfile: () => {},
});

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    // ✅ On mount, read from localStorage (already saved by verifyToken)
    const stored = localStorage.getItem('userProfile');
    if (stored) {
      try {
        setProfile(JSON.parse(stored));
      } catch {
        console.error("Failed to parse userProfile from localStorage");
      }
    }
  }, []);

  return (
    <UserProfileContext.Provider value={{ profile, setProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => useContext(UserProfileContext);